<template>
  <Page class="px-6 w-full" :title="translate(`${LOCALE_PREFIX}.pageTitle`)">
    <div class="flex flex-col items-center justify-center gap-2 mt-8 mb-4">
      <h1 class="font-semibold">
        {{ header }}
      </h1>
      <p
        v-if="!isSendPasswordLink && !isUserVerified"
        class="font-sm text-[1rem] email-instructions-color no-line-height"
      >
        {{ translate(`${LOCALE_PREFIX}.info.emailResetInstructions`) }}
      </p>
    </div>

    <template v-if="isUserVerified">
      <ChangePasswordForm :onSendResetPassword="sendResetPassword" />
    </template>

    <template v-else>
      <template v-if="!isSendPasswordLink">
        <PasswordResetForm :on-send-reset-email="sendResetEmail" :processing="processing" />
      </template>

      <template v-else>
        <div class="flex flex-col mb-4 email-instructions-color">
          <span class="font-sm text-[1rem]">{{
            translate(`${LOCALE_PREFIX}.info.sentInstructionsTo`)
          }}</span>
          <span class="submitted-email font-sm text-[1rem] font-bold">{{ submittedEmail }}</span>
          <p class="font-sm text-[1rem]">
            {{ translate(`${LOCALE_PREFIX}.info.waitForArrivalCheckSpam`) }}
          </p>
        </div>
      </template>

      <template v-if="!isSendPasswordLink">
        <GoToSso />
      </template>

      <template v-else>
        <div class="flex items-center justify-center w-full">
          <Button
            id="centricOS-resend-reset-link-button"
            class="w-full"
            :label="translate(`${LOCALE_PREFIX}.buttons.resendResetLink`)"
            @click="resendEmail({ email: submittedEmail })"
          />
        </div>

        <div class="flex items-center justify-center mt-4 w-full">
          <Divider align="center" type="solid">
            <span class="uppercase">{{ translate(`${LOCALE_PREFIX}.orHelperText`) }}</span>
          </Divider>
        </div>

        <div class="flex items-center justify-center">
          <Button
            id="centricOS-go-back-button"
            :class="`${ButtonStateClass.LINK}`"
            :label="translate(`${LOCALE_PREFIX}.buttons.goBackChangeEmail`)"
            @click="backToChangeEmail"
          >
            <template #icon>
              <i class="material-symbols-rounded mr-2">arrow_back</i>
            </template>
          </Button>
        </div>
      </template>
    </template>
  </Page>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useAuthStore } from '@centric-os/stores/auth/use-auth-store';
import { LoginLocalePrefix } from '../../../enums';
import PasswordResetForm from '../password-reset-form/PasswordResetForm.vue';
import { ButtonStateClass } from '@centric-os/components/src/core/buttons/enums';
import useGlobalToast from '@centric-os/components/src/core/messages/toast/composables/use-global-toast';
import { useGenericAPIErrorToast, useTranslation } from '@centric-os/helpers';
import GoToSso from '../go-to-sso/GoToSso.vue';
import ChangePasswordForm from '../change-password-form/ChangePasswordForm.vue';
import { useRouter } from 'vue-router';

const authStore = useAuthStore();
const { translate } = useTranslation();
const router = useRouter();

const isSendPasswordLink = ref<boolean>(false);
const submittedEmail = ref<string | null>(null);
const isUserVerified = ref<boolean>(false);
const realm = ref<string | null>(null);
const userId = ref<string | null>(null);
const resetToken = ref<string | null>(null);
const stage = ref<string | null>(null);
const role = ref<string | null>(null);
const LOCALE_PREFIX = LoginLocalePrefix.PASSWORD_RESET;
defineExpose({ submittedEmail, isSendPasswordLink });

const header = computed<string>(() => {
  let translatedText: string;

  if (isSendPasswordLink.value) {
    translatedText = translate(`${LOCALE_PREFIX}.info.checkYourEmail`);
  } else if (isUserVerified.value) {
    translatedText = translate(`${LOCALE_PREFIX}.resetPasswordTitle`);
  } else {
    translatedText = translate(`${LOCALE_PREFIX}.forgotPasswordTitle`);
  }

  return translatedText;
});

const { showErrorToast, showSuccessToast } = useGlobalToast();
const processing = ref<boolean>(false);

const sendResetEmail = async (formData: { email: string }): Promise<void> => {
  submittedEmail.value = formData.email;
  processing.value = true;
  try {
    await authStore.sendUserForgotPassword({ email: formData.email });
    isSendPasswordLink.value = !isSendPasswordLink.value;
    handleSuccess(`${LOCALE_PREFIX}.form.success.emailSent`);
    processing.value = false;
  } catch (err: unknown) {
    processing.value = false;
    showErrorToast(
      (err instanceof Error ? err.message : undefined) ||
        translate(`${LOCALE_PREFIX}resetToken.form.errors.unexpectedError`),
    );
    throw err;
  }
};

const resendEmail = async ({ email }: { email: string }): Promise<void> => {
  try {
    await authStore.sendUserForgotPassword({ email });
    handleSuccess(`${LOCALE_PREFIX}.form.success.emailSent`);
  } catch (err: unknown) {
    showErrorToast(
      (err instanceof Error ? err.message : undefined) ||
        translate(`${LOCALE_PREFIX}.form.errors.unexpectedError`),
    );
    throw err;
  }
};

const sendResetPassword = async ({
  password,
  confirmPassword,
}: {
  password: string;
  confirmPassword: string;
}): Promise<void> => {
  try {
    await authStore.resetPassword({
      newPassword: confirmPassword,
      resetToken: resetToken.value,
      userId: userId.value,
      realm: realm.value,
      stage: stage.value,
      role: role.value,
    });
    authStore.isPasswordReset = true;
    handleSuccess(`${LOCALE_PREFIX}.form.success.passwordReset`);
    router.push('/login');
  } catch (err: unknown) {
    showErrorToast(
      (err instanceof Error ? err.message : undefined) ||
        translate(`${LOCALE_PREFIX}.form.errors.unexpectedError`),
    );
    throw err;
  }
};

const handleSuccess = async (successMessageKey: string): Promise<void> => {
  showSuccessToast(translate(successMessageKey));
};

useGenericAPIErrorToast(showErrorToast);

const backToChangeEmail: () => void = () => {
  isSendPasswordLink.value = !isSendPasswordLink.value;
};

onMounted(async (): Promise<void> => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const userVerified = urlParams.get('user_verified');
    userId.value = urlParams.get('id');
    resetToken.value = urlParams.get('reset_token');
    realm.value = urlParams.get('realm');
    stage.value = urlParams.get('stage');
    role.value = urlParams.get('role');
    authStore.isPasswordReset = false;

    if (userVerified === null) {
      isUserVerified.value = false;
    } else {
      isUserVerified.value = true;
    }
  } catch (err: unknown) {
    showErrorToast(translate(`${LOCALE_PREFIX}.form.errors.unexpectedError`));
    throw err;
  }
});
</script>

<style lang="scss" scoped>
.email-instructions-color {
  color: $neutralDarkD2Color;
}

.no-line-height {
  line-height: 0;
}
</style>
